import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowsClockwise, FileXls, ChartBar } from '@phosphor-icons/react';

export function ProcurementRequirement() {
  const [jos, setJos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [expandedMat, setExpandedMat] = useState(null);

  const fetchJOs = useCallback(async () => {
    try {
      const { data } = await api.get('/api/inventory/job-orders?inv_status=bom_generated');
      // Also include verified, stock_calculated etc
      const { data: data2 } = await api.get('/api/inventory/job-orders?inv_status=verified');
      const { data: data3 } = await api.get('/api/inventory/job-orders?inv_status=stock_calculated');
      const all = [...data, ...data2, ...data3];
      const unique = Array.from(new Map(all.map(j => [j.jo_number, j])).values());
      setJos(unique);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load JOs');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchJOs(); }, [fetchJOs]);

  const toggleSelect = (joNum) => {
    setSelected(prev => prev.includes(joNum) ? prev.filter(j => j !== joNum) : [...prev, joNum]);
  };

  const selectAll = () => {
    if (selected.length === jos.length) setSelected([]);
    else setSelected(jos.map(j => j.jo_number));
  };

  const calculate = async () => {
    if (!selected.length) { toast.error('Select at least one JO'); return; }
    setCalculating(true);
    try {
      const { data } = await api.post('/api/inventory/procurement-requirement', { jo_numbers: selected });
      setResults(data);
      toast.success(`Calculated requirement for ${data.length} materials`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Calculation failed');
    } finally { setCalculating(false); }
  };

  const exportCSV = () => {
    if (!results.length) return;
    const headers = ['Material Name', 'Category', 'UQC', 'Required Qty', 'Available Stock', 'Reserved Stock', 'Current Stock', 'Shortage'];
    const rows = results.map(r => [r.material_name, r.category, r.uqc, r.required_qty, r.available_stock, r.reserved_stock, r.current_stock, r.shortage]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `procurement_requirement_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="space-y-4 pb-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">Procurement Requirement</h1>
            <p className="text-sm text-zinc-500 mt-2">Calculate material requirement across selected Job Orders</p>
          </div>
          <Button onClick={fetchJOs} variant="outline" className="h-10">
            <ArrowsClockwise size={16} weight="bold" className="mr-1.5" /> Refresh
          </Button>
        </div>

        {/* JO Selection */}
        <Card className="rounded-sm shadow-sm">
          <CardHeader className="py-2 px-4 bg-zinc-50 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-bold">Select Job Orders ({selected.length} selected)</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={selectAll}>
                  {selected.length === jos.length ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  data-testid="calc-procurement"
                  size="sm"
                  onClick={calculate}
                  disabled={calculating || !selected.length}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                >
                  <ChartBar size={14} className="mr-1" /> {calculating ? 'Calculating...' : 'Calculate Requirement'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-center text-zinc-400">Loading JOs with BOM...</div>
            ) : jos.length === 0 ? (
              <div className="p-6 text-center text-zinc-400">No JOs with BOM generated. Generate BOM first from the JO detail page.</div>
            ) : (
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-zinc-50">
                    <tr className="border-b">
                      <th className="p-2 w-10"><input type="checkbox" checked={selected.length === jos.length && jos.length > 0} onChange={selectAll} /></th>
                      <th className="p-2 text-left text-xs font-bold uppercase text-zinc-500">JO Number</th>
                      <th className="p-2 text-left text-xs font-bold uppercase text-zinc-500">Customer</th>
                      <th className="p-2 text-xs font-bold uppercase text-zinc-500">Windows</th>
                      <th className="p-2 text-left text-xs font-bold uppercase text-zinc-500">Inv Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jos.map(jo => (
                      <tr key={jo.jo_number} className={`border-b hover:bg-zinc-50 ${selected.includes(jo.jo_number) ? 'bg-orange-50' : ''}`}>
                        <td className="p-2 text-center">
                          <input type="checkbox" checked={selected.includes(jo.jo_number)} onChange={() => toggleSelect(jo.jo_number)} />
                        </td>
                        <td className="p-2 font-bold">{jo.jo_number}</td>
                        <td className="p-2 text-zinc-700">{jo.customer_name}</td>
                        <td className="p-2 text-center">{jo.no_of_windows}</td>
                        <td className="p-2 text-xs font-bold text-orange-600">{jo.inventory_status?.replace(/_/g, ' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        {results.length > 0 && (
          <Card className="rounded-sm shadow-sm">
            <CardHeader className="py-2 px-4 bg-zinc-50 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-bold">Procurement Requirement ({results.length} materials)</CardTitle>
                <Button onClick={exportCSV} size="sm" variant="outline">
                  <FileXls size={14} className="mr-1" /> Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b">
                      <th className="p-2 text-left text-xs font-bold uppercase text-zinc-500">Material</th>
                      <th className="p-2 text-left text-xs font-bold uppercase text-zinc-500">Category</th>
                      <th className="p-2 text-xs font-bold uppercase text-zinc-500">UQC</th>
                      <th className="p-2 text-xs font-bold uppercase text-zinc-500">Required</th>
                      <th className="p-2 text-xs font-bold uppercase text-zinc-500">Available</th>
                      <th className="p-2 text-xs font-bold uppercase text-zinc-500">Reserved</th>
                      <th className="p-2 text-xs font-bold uppercase text-zinc-500">Current</th>
                      <th className="p-2 text-xs font-bold uppercase text-zinc-500">Shortage</th>
                      <th className="p-2 text-xs font-bold uppercase text-zinc-500">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, idx) => (
                      <React.Fragment key={r.material_name}>
                        <tr className={`border-b hover:bg-zinc-50 ${r.shortage > 0 ? 'bg-red-50' : ''}`}>
                          <td className="p-2 font-bold text-zinc-800">{r.material_name}</td>
                          <td className="p-2 text-zinc-600 text-xs">{r.category}</td>
                          <td className="p-2 text-center text-xs">{r.uqc}</td>
                          <td className="p-2 text-center font-bold text-blue-700">{r.required_qty?.toFixed(2)}</td>
                          <td className="p-2 text-center font-bold text-emerald-600">{r.available_stock?.toFixed(2)}</td>
                          <td className="p-2 text-center text-amber-600">{r.reserved_stock}</td>
                          <td className="p-2 text-center">{r.current_stock}</td>
                          <td className={`p-2 text-center font-bold ${r.shortage > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {r.shortage > 0 ? r.shortage.toFixed(2) : 'OK'}
                          </td>
                          <td className="p-2 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setExpandedMat(expandedMat === idx ? null : idx)}
                              className="h-6 px-2 text-xs"
                            >
                              {expandedMat === idx ? 'Hide' : 'JO Breakup'}
                            </Button>
                          </td>
                        </tr>
                        {expandedMat === idx && r.jo_breakup?.length > 0 && (
                          <tr key={`${r.material_name}-detail`}>
                            <td colSpan={9} className="p-0">
                              <div className="bg-zinc-100 p-3 mx-4 mb-2 rounded">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b">
                                      <th className="p-1 text-left font-bold text-zinc-500">JO Number</th>
                                      <th className="p-1 text-left font-bold text-zinc-500">Item</th>
                                      <th className="p-1 text-right font-bold text-zinc-500">Gross Qty</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.jo_breakup.map((b) => (
                                      <tr key={`${b.jo_number}-${b.item_name}`} className="border-b border-zinc-200">
                                        <td className="p-1 font-bold">{b.jo_number}</td>
                                        <td className="p-1">{b.item_name}</td>
                                        <td className="p-1 text-right font-bold">{b.gross_qty?.toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
